import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShedConfigurator } from 'shed-configurator';
import { leadsAPI } from '../services/api/leadsAPI';
import { supabase } from '../lib/supabase';
import { DEALER_ID } from '../constants/dealer';
import { builderConfig } from '../constants/builderConfig';
import { getModelSlug } from '../constants/shedModels';
import StripeCheckout from '../components/StripeCheckout';

/**
 * /configure — public, embeddable shed configuration wizard.
 *
 * The flow ends in SE Yard Solutions' real ecommerce checkout: the configured
 * shed is handed to the same <StripeCheckout> modal the storefront uses, so the
 * customer reserves their custom build with a Stripe deposit (25%). The custom
 * shed isn't an inventory row — the stripe-create-checkout edge function builds
 * the line item from the amount/name we pass, so no inventory record is needed.
 *
 * In parallel we still persist the full configuration as a `leads` row via
 * leadsAPI.addLead() (best-effort, non-blocking) so it lands in the admin Leads
 * tab Edwin already works — the CRM record of a high-intent quote, even if the
 * customer abandons payment.
 */
const ConfigureShed = () => {
  const [searchParams] = useSearchParams();
  const [dealer, setDealer] = useState(null);
  const [checkoutShed, setCheckoutShed] = useState(null);
  const [checkoutCustomer, setCheckoutCustomer] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  // The single-dealer storefront only ever sells as Edwin. Load his dealer row
  // (id + Stripe connection state) so the checkout modal can open against it.
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('dealers')
        .select('id, company_name, stripe_connected')
        .eq('id', DEALER_ID)
        .single();
      if (active && data) setDealer(data);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Best-effort deep-link prefill from /configure?width=..&depth=..
  // (ShedDetail/ShedCheckout build these links when a customer wants
  //  "one just like it"). Only feet-as-numbers map cleanly to dimensions.
  const initialDimensions = useMemo(() => {
    const w = parseFloat(searchParams.get('width'));
    const l = parseFloat(searchParams.get('depth'));
    if (Number.isFinite(w) && w > 0 && Number.isFinite(l) && l > 0) {
      return { w, l };
    }
    return undefined;
  }, [searchParams]);

  // Which model the customer is building — resolved from the storefront link
  // (?model=The Spartan / spartan / …) to the catalog slug used for base pricing.
  const initialModel = useMemo(
    () => getModelSlug(searchParams.get('model')) ?? undefined,
    [searchParams],
  );

  // CRM record of the configuration — fire-and-forget so a leads RLS/schema
  // hiccup never blocks the customer from reaching checkout.
  const saveLead = useCallback((quote) => {
    const { model, dimensions, selections, totals, lead, meta } = quote;

    const selectionLines = selections
      .map((li) => `  • ${li.label}: ${li.detail} — ${li.price}`)
      .join('\n');

    const message = [
      `SHED CONFIGURATOR — ${model.name}`,
      `Size: ${dimensions.w} × ${dimensions.l} ft (${dimensions.floorSqFt} sq ft floor)`,
      `Configured total: ${totals.total}`,
      '',
      'Configuration:',
      selectionLines,
      '',
      `Customer ZIP: ${lead.zip}`,
      `Sent to checkout from online configurator (v${meta.configuratorVersion}) on ${meta.createdAt}`,
    ].join('\n');

    const quoteValue = Math.round(totals.totalCents) / 100;

    const leadData = {
      first_name: lead.firstName,
      last_name: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      lead_type: 'information',
      lead_source: 'website',
      status: 'new',
      message,
      budget_min: quoteValue,
      budget_max: quoteValue,
      notes: [
        {
          id: meta.createdAt,
          text: JSON.stringify(quote, null, 2),
          created_at: meta.createdAt,
          created_by: 'Shed Configurator',
        },
      ],
    };

    leadsAPI.addLead(leadData).catch((err) => {
      console.error('Configurator lead save failed (checkout continues):', err);
    });
  }, []);

  const handleQuoteSubmit = useCallback(
    async (quote) => {
      const { model, dimensions, totals, lead } = quote;
      const fullPrice = Math.round(totals.totalCents) / 100;

      // Synthesize the shed the checkout/edge function expects. No inventory id —
      // the edge function prices off `amount`/`shed_name`, not a DB lookup.
      const shed = {
        id: null,
        name: `Custom ${model.name}`,
        size: `${dimensions.w} × ${dimensions.l} ft`,
        shedType: model.name,
        price: fullPrice,
        salePrice: null,
        depositAmount: Math.round(fullPrice * 0.25),
        images: [],
      };

      setCheckoutShed(shed);
      setCheckoutCustomer({
        name: `${lead.firstName} ${lead.lastName}`.trim(),
        email: lead.email,
        phone: lead.phone,
      });

      // Keep the CRM record, then open the real ecommerce checkout.
      saveLead(quote);
      setShowCheckout(true);
    },
    [saveLead],
  );

  return (
    <>
      <Helmet>
        <title>Build Your Shed | HOME DEPOT SHEDS</title>
        <meta
          name="description"
          content="Design your custom shed — pick the model, size, paint, roofing and add-ons, then reserve your build with a secure deposit."
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-[1440px] mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                Build Your Shed
              </h1>
              <p className="mt-2 text-gray-600">
                Configure it your way, then reserve your custom build with a secure deposit.
              </p>
            </div>

            <ShedConfigurator
              config={builderConfig}
              initialModel={initialModel}
              initialDimensions={initialDimensions}
              theme={{ primary: '#059669' }}
              onQuoteSubmit={handleQuoteSubmit}
            />
          </div>
        </div>
      </div>

      <StripeCheckout
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        shed={checkoutShed}
        dealer={dealer}
        paymentType="deposit"
        customerInfo={checkoutCustomer}
      />
    </>
  );
};

export default ConfigureShed;
